import { useEffect, useState } from 'react'

export type OutlineStyle = 'detailed' | 'basic'

const STORAGE_KEY = 'todone.project.outlineStyle'
const DEFAULT_STYLE: OutlineStyle = 'basic'
const VALID: readonly OutlineStyle[] = ['detailed', 'basic']

const readStored = (): OutlineStyle => {
  if (typeof window === 'undefined') return DEFAULT_STYLE
  const stored = window.localStorage.getItem(STORAGE_KEY) as OutlineStyle | null
  return stored && VALID.includes(stored) ? stored : DEFAULT_STYLE
}

/**
 * Persisted Project-viewer outline density. Two modes:
 *
 * - `detailed` (default) — the full `OutlineEditor` render. Cards, descriptions, attached
 *   materials/tools, photos, milestone chips, dependency badges. Best for "I'm building this
 *   right now and need every detail."
 * - `basic` — `OutlineBasic`. Minimal compact list: indented titles + tiny section / milestone
 *   / project-reference markers, nothing else. Best for "I just want to skim the structure of
 *   this build."
 */
export const useOutlineStyle = (): [OutlineStyle, (next: OutlineStyle) => void] => {
  const [style, setStyle] = useState<OutlineStyle>(readStored)
  useEffect(() => {
    if (typeof window === 'undefined') return
    window.localStorage.setItem(STORAGE_KEY, style)
  }, [style])
  return [style, setStyle]
}
